import type { ImageView } from '../generated';

/**
 * Fixed poll interval for image queries while any image is pending.
 * Replaces the old growing `1000 * (dataUpdateCount + 1)` backoff, whose
 * lifetime counter never reset and eventually killed polling permanently.
 */
export const IMAGE_POLL_INTERVAL_MS = 12_000;

/**
 * If an image has been pending (null URL / unscored) for longer than this,
 * we treat it as stuck and stop polling; the UI flips its skeleton to the
 * broken-image state. Same window as the Spark-grid cull staleness guard.
 */
export const PENDING_STALE_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * An image URL is pending when the resize variant hasn't landed yet (the API
 * head-checks S3 and returns null) or when the cached value is a non-image
 * placeholder such as the S3 upload URL from an optimistic insert.
 */
export function isImageUrlPending(image: Pick<ImageView, 'url'>): boolean {
  return image.url == null || !image.url.includes('/images');
}

export function isPastStaleWindow(timestamp: string | null | undefined): boolean {
  if (!timestamp) return false;
  return Date.now() - new Date(timestamp).getTime() > PENDING_STALE_TIMEOUT_MS;
}

/**
 * UI predicate: the image never got any URL within the stale window, so render
 * the broken-image state instead of a skeleton. Deliberately narrower than
 * `isImageUrlPending`: the '/images' placeholder heuristic exists to keep list
 * queries polling on a transient cache state (e.g. the S3 upload URL from an
 * optimistic insert), not to judge whether the render layer can load the URL.
 * A real, loadable URL that happens not to contain '/images' must never be
 * painted as broken. Anchored on `updated_at ?? created_at`: rescore and
 * refresh flows null the score on an old image but touch `updated_at`, so a
 * `created_at` anchor would flip them straight to broken.
 */
export function isImageUrlStale(image: Pick<ImageView, 'url' | 'created_at' | 'updated_at'>): boolean {
  return image.url == null && isPastStaleWindow(image.updated_at ?? image.created_at);
}

/**
 * Timestamps of the images a list query should keep polling for: any image
 * with a pending URL or a null vizit_score. Anchored on `updated_at ??
 * created_at` so rescore/refresh flows re-enter the poll window (see
 * `isImageUrlStale`).
 */
export function getPendingImageTimestamps(
  images: ReadonlyArray<Pick<ImageView, 'url' | 'vizit_score' | 'created_at' | 'updated_at'>>,
): Array<string | null> {
  return images
    .filter((image) => image.vizit_score === null || isImageUrlPending(image))
    .map((image) => image.updated_at ?? image.created_at ?? null);
}

interface PollWhilePendingOptions {
  interval?: number;
  onPoll?: (pendingCount: number) => void;
  onTimeout?: (pendingCount: number) => void;
}

/**
 * The slice of TanStack Query's Query instance the factory relies on: a
 * stable identity for the per-query state below, plus the ability to force a
 * refetch when a pending item crosses the stale deadline.
 */
interface PollableQuery {
  fetch: () => Promise<unknown>;
  getObserversCount: () => number;
}

// Keyed by the stable Query instance so state survives re-renders (each render
// builds a fresh refetchInterval closure) without leaking removed queries.
const firstPendingAt = new WeakMap<object, number>();
const timedOutQueries = new WeakSet<object>();
const polledQueries = new WeakSet<object>();
const staleDeadlineTimers = new WeakMap<object, ReturnType<typeof setTimeout>>();

function clearStaleDeadlineTimer(query: object): void {
  const timer = staleDeadlineTimers.get(query);
  if (timer !== undefined) {
    clearTimeout(timer);
    staleDeadlineTimers.delete(query);
  }
}

// The skeleton-to-broken flip is computed at render time, but nothing else
// guarantees a render at the deadline: the last in-window poll can return
// structurally identical data, so nothing re-renders and polling stops with
// the skeleton still up. Refetching when the next pending item crosses the
// stale window forces a state bump so the flip paints on its own.
function scheduleStaleDeadlineBump(query: PollableQuery, anchorsMs: ReadonlyArray<number>): void {
  const nowMs = Date.now();
  const nextDeadlineMs = Math.min(
    ...anchorsMs.map((anchorMs) => anchorMs + PENDING_STALE_TIMEOUT_MS).filter((deadlineMs) => deadlineMs > nowMs),
  );
  if (!Number.isFinite(nextDeadlineMs)) return;

  const timer = setTimeout(
    () => {
      staleDeadlineTimers.delete(query);
      if (query.getObserversCount() === 0) return;
      query.fetch().catch(() => undefined);
    },
    nextDeadlineMs - nowMs + 1,
  );
  staleDeadlineTimers.set(query, timer);
}

/**
 * Builds a `refetchInterval` callback: fixed-interval polling while any
 * pending item is within the stale window, `false` (zero steady-state cost)
 * otherwise. Items without their own timestamp are anchored on the moment
 * they were first observed pending. `onTimeout` fires once per pending
 * episode, and only for queries that actually polled and gave up — an item
 * already past the window on first evaluation just stops without it; the
 * window re-arms after the query fully resolves.
 */
export function pollWhilePending<TQuery extends PollableQuery>(
  getPendingTimestamps: (query: TQuery) => Array<string | null>,
  options: PollWhilePendingOptions = {},
): (query: TQuery) => number | false {
  const { interval = IMAGE_POLL_INTERVAL_MS, onPoll, onTimeout } = options;

  return (query) => {
    // Unit tests never background-poll — the same rule as the repo-wide
    // `retry: import.meta.env.VITEST ? 0 : …` idiom. Test suites share one
    // module-scope QueryClient under fake timers, and a poll fired during
    // teardown (afterEach runs pending timers) wedges the shared Query
    // in-flight, deduping the next test's mount refetch. Polling behavior is
    // unit-tested in @vizit/api with the env stubbed off (vi.stubEnv).
    if (import.meta.env.VITEST) return false;

    const pendingTimestamps = getPendingTimestamps(query);

    if (pendingTimestamps.length === 0) {
      clearStaleDeadlineTimer(query);
      firstPendingAt.delete(query);
      timedOutQueries.delete(query);
      polledQueries.delete(query);
      return false;
    }

    let fallbackAnchorMs = firstPendingAt.get(query);
    if (fallbackAnchorMs === undefined) {
      fallbackAnchorMs = Date.now();
      firstPendingAt.set(query, fallbackAnchorMs);
    }

    const anchorsMs = pendingTimestamps.map((timestamp) =>
      timestamp ? new Date(timestamp).getTime() : fallbackAnchorMs,
    );
    const anyWithinWindow = anchorsMs.some((anchorMs) => Date.now() - anchorMs <= PENDING_STALE_TIMEOUT_MS);

    clearStaleDeadlineTimer(query);

    if (anyWithinWindow) {
      polledQueries.add(query);
      scheduleStaleDeadlineBump(query, anchorsMs);
      onPoll?.(pendingTimestamps.length);
      return interval;
    }

    if (polledQueries.has(query) && !timedOutQueries.has(query)) {
      timedOutQueries.add(query);
      onTimeout?.(pendingTimestamps.length);
    }
    return false;
  };
}
